/**
 * Biond Meal Selection API - Frontend Integration
 *
 * Diese JavaScript-Datei bietet einfach zu verwendende Funktionen für die Integration
 * des Biond Meal Selection Systems in Shopify Themes.
 *
 * Verwendung:
 * 1. Diese Datei einbinden
 * 2. Die BiondMealAPI-Klasse verwenden, um mit der API zu interagieren
 *
 * Beispiel:
 * ```javascript
 * const api = new BiondMealAPI();
 * const subscription = await api.getSubscription('1234');
 * await api.saveMeals('1234', selectedMeals);
 * ```
 */

export class BiondMealAPI {
  constructor() {
    // Die Basis-URL für den Biond App Proxy
    this.baseUrl = "/apps/biond-appstle-proxy/api";

    // JWT Token für Authentifizierung (wird bei Bedarf gesetzt)
    this.jwtToken = null;
  }

  /**
   * JWT Token für Authentifizierung abrufen
   * Dies sollte vor jeder API-Anfrage aufgerufen werden
   */
  async getJWTToken() {
    if (this.jwtToken) {
      return this.jwtToken;
    }

    try {
      const response = await fetch(`${this.baseUrl}/generate-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          shopDomain: window.Shopify?.shop || "biond.de",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.jwtToken = data.token;
        return this.jwtToken;
      } else {
        throw new Error("Failed to get JWT token");
      }
    } catch (error) {
      console.error("Error getting JWT token:", error);
      throw error;
    }
  }

  /**
   * Abonnement-Details anhand der Bestell-ID abrufen (wie #1234)
   *
   * @param {string} orderId - Die Bestell-ID (z.B. "1234", "#1234", oder vollständige subscriptionContractId)
   * @param {Object} options - Optionale Parameter
   * @param {boolean} options.bypassDates - Datumsbeschränkungen umgehen (Standard: false)
   * @param {boolean} options.includeCancelled - Stornierte Abonnements einschließen (Standard: false)
   * @param {boolean} options.raw - Rohe Daten ohne Bereinigung zurückgeben (Standard: false)
   * @returns {Promise<Object>} Abonnement-Daten
   */
  async getSubscription(orderId, options = {}) {
    try {
      // Sicherstellen, dass wir einen JWT Token haben
      await this.getJWTToken();

      // Query-Parameter erstellen
      const params = new URLSearchParams();
      if (options.bypassDates) params.append("bypass_dates", "true");
      if (options.includeCancelled) params.append("includeCancelled", "true");
      if (options.raw) params.append("raw", "true");

      const queryString = params.toString();
      const url = `${this.baseUrl}/subscription-details/${orderId}?includeCancelled=true${queryString ? "&" + queryString : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-biond-cp-auth": this.jwtToken
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
      throw error;
    }
  }

  /**
   * Mahlzeiten zu einem Abonnement speichern
   *
   * @param {string} orderId - Die Bestell-ID (z.B. "1234", "#1234", oder vollständige subscriptionContractId)
   * @param {Array} selectedMeals - Array von ausgewählten Mahlzeiten
   * @param {string} selectedMeals[].variantId - Shopify Varianten-ID
   * @param {number} selectedMeals[].quantity - Anzahl der Mahlzeit
   * @param {Object} options - Optionale Parameter
   * @param {string} options.lineId - Spezifische Box-ID (lineId) zum Aktualisieren (optional - für mehrere Boxen)
   * @param {boolean} options.bypassDates - Datumsbeschränkungen umgehen (Standard: false)
   * @param {boolean} options.testMode - Testmodus - validieren aber nicht speichern (Standard: false)
   * @returns {Promise<Object>} Speicher-Ergebnis
   */
  async saveMeals(orderId, selectedMeals, options = {}) {
    try {
      // Sicherstellen, dass wir einen JWT Token haben
      await this.getJWTToken();

      // Query-Parameter erstellen
      const params = new URLSearchParams();
      if (options.bypassDates) params.append("bypass_dates", "true");
      if (options.testMode) params.append("test_mode", "true");
      const queryString = params.toString();
      const url = `${this.baseUrl}/subscription-meals/${orderId}${queryString ? "?" + queryString : ""}`;

      // Request-Body vorbereiten
      const requestBody = {};
      if (options.lineId) {
        requestBody.lineId = options.lineId;
        requestBody.variantQuantityList = selectedMeals;
      } else {
        // Auto-Fill Modus - kein Body nötig, Mahlzeiten werden automatisch ausgewählt
        requestBody.variantQuantityList = selectedMeals;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-biond-cp-auth": this.jwtToken,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }
    } catch (error) {
      console.error("Error saving meals:", error);
      throw error;
    }
  }

  /**
   * Verfügbare Boxen für ein Abonnement abrufen
   * Hilft bei der Identifizierung mehrerer Boxen in einem Abonnement
   *
   * @param {string} orderId - Die Bestell-ID (z.B. "1234", "#1234", oder vollständige subscriptionContractId)
   * @param {Object} options - Optionale Parameter
   * @param {boolean} options.bypassDates - Datumsbeschränkungen umgehen (Standard: false)
   * @param {boolean} options.includeCancelled - Stornierte Abonnements einschließen (Standard: false)
   * @returns {Promise<Array>} Array von verfügbaren Boxen mit lineId, title, etc.
   */
  async getAvailableBoxes(orderId, options = {}) {
    try {
      const subscription = await this.getSubscription(orderId, options);

      if (subscription.success && subscription.data.lineItems) {
        // Filter nur Boxen (nicht einzelne Mahlzeiten)
        const boxes = subscription.data.lineItems.filter((item) => item.isBox);
        return {
          success: true,
          boxes: boxes,
          totalBoxes: boxes.length,
        };
      } else {
        return {
          success: false,
          error: subscription.error || "Keine Boxen gefunden",
          boxes: [],
        };
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der verfügbaren Boxen:", error);
      return {
        success: false,
        error: error.message,
        boxes: [],
      };
    }
  }

  /**
   * Mahlzeiten für ein Abonnement automatisch basierend auf Benutzereinstellungen ausfüllen
   * Dies wählt automatisch Mahlzeiten basierend auf den Quiz-Einstellungen des Abonnements aus
   *
   * @param {string} orderId - Die Bestell-ID (z.B. "1234", "#1234", oder vollständige subscriptionContractId)
   * @param {Object} options - Optionale Parameter
   * @param {boolean} options.bypassDates - Datumsbeschränkungen umgehen (Standard: false)
   * @param {boolean} options.testMode - Testmodus - validieren aber nicht speichern (Standard: false)
   * @returns {Promise<Object>} Auto-Fill Ergebnis
   */
  async autoFillMeals(orderId, options = {}) {
    try {
      // Sicherstellen, dass wir einen JWT Token haben
      await this.getJWTToken();

      // Query-Parameter erstellen
      const params = new URLSearchParams();
      if (options.bypassDates) params.append("bypass_dates", "true");
      if (options.testMode) params.append("test_mode", "true");

      const queryString = params.toString();
      const url = `${this.baseUrl}/subscription-meals/${orderId}${queryString ? "?" + queryString : ""}`;

      // Auto-Fill Modus - kein Body nötig
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-biond-cp-auth": this.jwtToken,
        },
        // Kein Body für Auto-Fill Modus
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }
    } catch (error) {
      console.error("Error auto-filling meals:", error);
      throw error;
    }
  }
}
/*
// Global verfügbar machen
window.BiondMealAPI = BiondMealAPI;

// Export für Modulsysteme
if (typeof module !== "undefined" && module.exports) {
  module.exports = BiondMealAPI;
}*/

/**
 * VERWENDUNGSBEISPIELE
 *
 * 1. Abonnement-Details abrufen:
 * ```javascript
 * const api = new BiondMealAPI();
 * const subscription = await api.getSubscription('1234');
 * console.log('Abonnement:', subscription.data);
 * ```
 *
 * 2. Verfügbare Boxen abrufen (für mehrere Boxen):
 * ```javascript
 * const api = new BiondMealAPI();
 * const boxes = await api.getAvailableBoxes('1234');
 * console.log('Verfügbare Boxen:', boxes.boxes);
 * boxes.boxes.forEach(box => {
 *   console.log(`Box: ${box.title} (ID: ${box.lineId})`);
 * });
 * ```
 *
 * 3. Mahlzeiten für eine spezifische Box speichern:
 * ```javascript
 * const api = new BiondMealAPI();
 * const selectedMeals = [
 *   { variantId: '55684257513847', quantity: 1 },
 *   { variantId: '55673183109495', quantity: 2 }
 * ];
 * // Für eine bestimmte Box (wenn mehrere vorhanden)
 * const result = await api.saveMeals('1234', selectedMeals, {
 *   lineId: 'specific-box-line-id'
 * });
 * console.log('Mahlzeiten gespeichert:', result);
 * ```
 *
 * 4. Mahlzeiten basierend auf Einstellungen automatisch ausfüllen:
 * ```javascript
 * const api = new BiondMealAPI();
 * const result = await api.autoFillMeals('1234');
 * console.log('Automatisch ausgefüllte Mahlzeiten:', result);
 * ```
 *
 * 5. Testmodus (validieren ohne zu speichern):
 * ```javascript
 * const api = new BiondMealAPI();
 * const result = await api.saveMeals('1234', selectedMeals, { testMode: true });
 * console.log('Test-Ergebnis:', result);
 * ```
 *
 * 6. Datumsbeschränkungen umgehen:
 * ```javascript
 * const api = new BiondMealAPI();
 * const subscription = await api.getSubscription('1234', { bypassDates: true });
 * ```
 */
